import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, TextField, Typography } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Page, Header, CustomButton, SecaoGenerica, ModalConfirmacao, SelecionarData } from 'components'
import { cutMaxLength } from 'utils/helpers'
import { FileManager } from 'components/FileManager'
import { SelectUsuarios } from 'components/SelectUsuarios'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import moment from 'moment'
import { useParams } from 'react-router'

import { useExumarCadaver } from './hooks/useExumarCadaver'
import useStyles from './ExumarCadaver.styles'

const maxLength250 = (newValue) => cutMaxLength(newValue, 250)
const maxLength4000 = (newValue) => cutMaxLength(newValue, 4000)

const MainForm = (props) => {
  const { route, arquivos, idCadaver, children } = props
  const classes = useStyles()

  const {
    formValues,
    formErrors,
    agentes,
    processandoOperacao,
    espelhoAberto,
    setEspelhoAberto,
    handleChangeCampoData,
    handleChangeNewValueByNameProperty,
    handleChangeWithModification,
    handleChangeAgentes,
    confirmarOperacao,
    hasError,
    router,
  } = useExumarCadaver({ route, idCadaver })

  const handleClickBotaoConfirmar = useCallback(() => {
    setEspelhoAberto(true)
  }, [setEspelhoAberto])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />

      <Grid container spacing={2}>
        {/* Ficha de Exumação - Renderizada de forma plana sem SecaoGenerica e sem título de bloco */}
        <Grid item md={6} sm={6} xs={12} className={classes.campoFormulario}>
          <AutocompleteNative
            name="tipoExumacao"
            noOptionsText="Sem informações"
            options={['JUDICIAL', 'ADMINISTRATIVA']}
            value={formValues.tipoExumacao || null}
            onChange={(event, newValue) => {
              handleChangeNewValueByNameProperty(event, newValue, 'tipoExumacao')
            }}
            getOptionLabel={(option) => option}
            renderOption={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="TIPO DA EXUMAÇÃO"
                error={hasError('tipoExumacao')}
                helperText={hasError('tipoExumacao') ? formErrors.tipoExumacao[0] : null}
                required
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid item md={6} sm={6} xs={12} className={classes.campoFormulario}>
          <SelecionarData
            dataBD={formValues.dataExumacao}
            withoutTime
            name="dataExumacao"
            callback={(dataExumacao) => handleChangeCampoData('dataExumacao', dataExumacao)}
            label="DATA DA EXUMAÇÃO"
            required
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
          <TextField
            value={formValues.motivo || ''}
            name="motivo"
            rows={5}
            multiline
            error={hasError('motivo')}
            variant="outlined"
            label="MOTIVO"
            helperText={hasError('motivo') ? formErrors.motivo[0] : null}
            fullWidth
            required
            onChange={(e) => handleChangeWithModification(e, maxLength4000)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Typography variant="caption">
            Caracteres restantes: {4000 - (formValues.motivo?.length ? formValues.motivo?.length : 0)}
          </Typography>
        </Grid>

        {/* DADOS DO SOLICITANTE - SecaoGenerica obrigatória a partir daqui */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="DADOS DO SOLICITANTE">
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('nomeSolicitante')}
                  fullWidth
                  multiline
                  required
                  helperText={hasError('nomeSolicitante') ? formErrors.nomeSolicitante[0] : null}
                  label="NOME COMPLETO"
                  name="nomeSolicitante"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.nomeSolicitante || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant="caption">
                  Caracteres restantes: {250 - (formValues.nomeSolicitante?.length ? formValues.nomeSolicitante?.length : 0)}
                </Typography>
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <AutocompleteNative
                  name="tipoDocSolicitante"
                  noOptionsText="Sem informações"
                  options={[
                    'REGISTRO CIVIL',
                    'CPF',
                    'CNH',
                    'CARTEIRA DE TRABALHO',
                    'CARTEIRA PROFISSIONAL',
                    'PASSAPORTE',
                    'CARTEIRA DE IDENTIFICAÇÃO FUNCIONAL',
                    'OUTRO',
                  ]}
                  value={formValues.tipoDocSolicitante || null}
                  onChange={(event, newValue) => {
                    handleChangeNewValueByNameProperty(event, newValue, 'tipoDocSolicitante')
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TIPO DE DOCUMENTO"
                      error={hasError('tipoDocSolicitante')}
                      helperText={hasError('tipoDocSolicitante') ? formErrors.tipoDocSolicitante[0] : null}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('docSolicitante')}
                  fullWidth
                  multiline
                  helperText={hasError('docSolicitante') ? formErrors.docSolicitante[0] : null}
                  label="DOCUMENTO"
                  name="docSolicitante"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.docSolicitante || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('orgaoExpedidorDocSolicitante')}
                  fullWidth
                  multiline
                  helperText={
                    hasError('orgaoExpedidorDocSolicitante') ? formErrors.orgaoExpedidorDocSolicitante[0] : null
                  }
                  label="ÓRGÃO EXPEDIDOR"
                  name="orgaoExpedidorDocSolicitante"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.orgaoExpedidorDocSolicitante || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* AGENTES RESPONSÁVEIS - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="AGENTES RESPONSÁVEIS">
            <Grid container spacing={2}>
              <Grid className={classes.campoFormulario} item md={12} sm={12} xs={12}>
                <SelectUsuarios
                  usuariosExternos={agentes}
                  setUsuariosExternos={handleChangeAgentes}
                  label="SELECIONE OS AGENTES"
                  isFromBanco={false}
                  multiplo={true}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* ARQUIVOS RELACIONADOS - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="ARQUIVOS RELACIONADOS">
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12} style={{ marginTop: '16px' }}>
                {children}
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* Ações / Botões inferiores */}
        <Grid item md={12} sm={12} xs={12}>
          <div className={classes.botoesFormulario}>
            <div className={classes.botaoContainer}>
              <CustomButton
                colorType="primary"
                variant="outlined"
                size="medium"
                onClick={() => router.history.goBack()}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </CustomButton>
            </div>

            <div className={classes.botaoContainer}>
              <CustomButton
                colorType="secondary"
                variant="contained"
                size="medium"
                disabled={processandoOperacao || Object.keys(formErrors).length > 0}
                isLoading={processandoOperacao}
                onClick={handleClickBotaoConfirmar}
                endIcon={<CheckIcon />}
              >
                CADASTRAR
              </CustomButton>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Modal de confirmação (Substituto do Espelho) */}
      <ModalConfirmacao
        aberto={espelhoAberto}
        onClose={() => {
          if (!processandoOperacao) {
            setEspelhoAberto(false)
          }
        }}
        onConfirm={() => confirmarOperacao(arquivos)}
        processando={processandoOperacao}
        titulo="Confirmação de dados"
        confirmarLabel="Confirmar"
        cancelarLabel="Voltar"
        confirmarColor="secondary"
        maxWidth="md"
      >
        <Grid container spacing={2} className={classes.dadosEspelho}>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>DATA DA EXUMAÇÃO</b>
            </Typography>
            <Typography variant="body1">
              {formValues.dataExumacao ? moment(formValues.dataExumacao).format('DD/MM/YYYY') : ''}
            </Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>TIPO DA EXUMAÇÃO</b>
            </Typography>
            <Typography variant="body1">{formValues.tipoExumacao}</Typography>
          </Grid>
          <Grid item md={12} sm={12} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>MOTIVO</b>
            </Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {formValues.motivo}
            </Typography>
          </Grid>
          <Grid item md={12} sm={12} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>SOLICITANTE</b>
            </Typography>
            <Typography variant="body1">{formValues.nomeSolicitante}</Typography>
          </Grid>
          {formValues.tipoDocSolicitante && (
            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>TIPO DO DOCUMENTO</b>
              </Typography>
              <Typography variant="body1">{formValues.tipoDocSolicitante}</Typography>
            </Grid>
          )}
          {formValues.docSolicitante && (
            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>DOCUMENTO</b>
              </Typography>
              <Typography variant="body1">{formValues.docSolicitante}</Typography>
            </Grid>
          )}
          {formValues.orgaoExpedidorDocSolicitante && (
            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>ÓRGÃO EXPEDIDOR</b>
              </Typography>
              <Typography variant="body1">{formValues.orgaoExpedidorDocSolicitante}</Typography>
            </Grid>
          )}
        </Grid>
      </ModalConfirmacao>
    </Page>
  )
}

MainForm.propTypes = {
  route: PropTypes.shape({
    titulo: PropTypes.string.isRequired,
    secao: PropTypes.string,
    origem: PropTypes.string,
  }).isRequired,
  arquivos: PropTypes.array.isRequired,
  idCadaver: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

export const ExumarCadaver = (props) => {
  const { route } = props
  const params = useParams()
  const [arquivos, setArquivos] = useState([])

  // Callback estável com validação de igualdade superficial para prevenir loops infinitos no FileManager
  const handleSetArquivos = useCallback((novosArquivos) => {
    setArquivos((prev) => {
      const isIdentical =
        novosArquivos.length === prev.length &&
        novosArquivos.every((val, idx) => val === prev[idx])

      return isIdentical ? prev : novosArquivos
    })
  }, [])

  return (
    <MainForm arquivos={arquivos} route={route} idCadaver={params.id}>
      <FileManager
        rotaUpload="/arquivo/cadaver"
        rotaLoadImagem="/arquivo/cadaver"
        setArquivosPai={handleSetArquivos}
        arquivosIniciais={[]}
        tipos={['image', 'pdf']}
      />
    </MainForm>
  )
}

ExumarCadaver.propTypes = {
  route: PropTypes.shape({
    titulo: PropTypes.string.isRequired,
    secao: PropTypes.string,
    origem: PropTypes.string,
  }).isRequired,
}
